import { resourceTypeGuards } from '../utils/medicalDataUtils';
import { MedicalDataTypes } from '../primary-record-types';

import { getIntl } from '../utils/intl';
import useCareTeamMember from './api/useCareTeamMember';
import { getCareTeamMemberDescriptorDisplay } from '../utils/careTeamUtils';
import { visitTypeMessages } from '../utils/sharedMessages';

const intl = getIntl('use-visit-name', {
    with: 'with',
    visit: 'Visit',
    for: 'for',
});

export type UseVisitNameArgs = {
    resource: MedicalDataTypes.PrimaryRecordResource | null;
    medicalProfileId: string;
};

/**
 * Evaluates visit name from any (potentially not-yet-loaded resource).  If it is not a visit or not yet loaded, returns null.
 */
const useVisitName = ({ resource, medicalProfileId }: UseVisitNameArgs): string | null => {
    const asVisitOrNull = resource && resourceTypeGuards.isVisit(resource) ? resource : null;
    const { careTeamMember } = useCareTeamMember({
        medicalProfileId,
        careTeamMemberId: asVisitOrNull?.providerCareTeamMemberId || null,
    });

    if (!asVisitOrNull) return null;

    // A custom "Other" label replaces the type in the name ("Xray with IU North for ...").
    const visitType =
        asVisitOrNull.visitType === 'other' && asVisitOrNull.visitTypeOtherLabel
            ? asVisitOrNull.visitTypeOtherLabel
            : asVisitOrNull.visitType
            ? visitTypeMessages(asVisitOrNull.visitType)
            : intl('visit');

    const provider = careTeamMember
        ? getCareTeamMemberDescriptorDisplay(careTeamMember)
        : asVisitOrNull.providerName || '';

    const reason = asVisitOrNull.reason || '';

    let returnValue = visitType;
    if (provider) {
        returnValue += ` ${intl('with')} ${provider}`;
    }

    if (reason) {
        returnValue += ` ${intl('for')} ${reason}`;
    }

    return returnValue;
};

export default useVisitName;
